import { Route } from '@angular/router';
import { MsalGuard } from '@azure/msal-angular';
import { FailedComponent } from './components/failed/failed.component';
import { PermissionsGuard } from './guards/PermissionsGuard';
import { WizardType } from '@voice/utils';
import { FaxAuditComponent } from './components/billing/fax-audit/fax-audit.component';
import { HostedSummaryComponent } from './components/billing/hosted-summary/hosted-summary.component';
import { TrunkingSummaryComponent } from './components/billing/trunking-summary/trunking-summary.component';
import { HostedSnapshotsComponent } from './components/billing/hosted-snapshots/hosted-snapshots.component';
import { BillingWizardComponent } from './components/billing/billing-wizard/billing-wizard.component';
import { BillingDashboardComponent } from './components/billing/billing-wizard/billing-dashboard.component';
import { OnboardingWizardComponent } from './components/billing/client-onboarding/onboarding-wizard.component';
import { RecordsListComponent } from './components/billing/client-onboarding/records-list.component';

export const appRoutes: Route[] = [
  {
    path: 'dashboard',
    canActivate: [MsalGuard],
    loadComponent: () =>
      import('./components/dashboard/dashboard.component').then(
        (c) => c.DashboardComponent
      ),
  },
  {
    path: 'login-failed',
    component: FailedComponent,
  },
  {
    path: 'domains',
    canActivate: [MsalGuard, PermissionsGuard],
    loadComponent: () =>
      import('./components/domains/domains.component').then(
        (c) => c.DomainsComponent
      ),
  },
  {
    path: 'domain/:id',
    canActivate: [MsalGuard, PermissionsGuard],
    loadComponent: () =>
      import('./components/domain/domain.component').then(
        (c) => c.DomainComponent
      ),
  },
  {
    path: 'sites',
    canActivate: [MsalGuard, PermissionsGuard],
    loadComponent: () =>
      import('./components/sites/sites.component').then(
        (c) => c.SitesComponent
      ),
  },
  {
    path: 'users',
    canActivate: [MsalGuard, PermissionsGuard],
    loadComponent: () =>
      import('./components/users/users.component').then(
        (c) => c.UsersComponent
      ),
  },
  {
    path: 'user-roles',
    canActivate: [MsalGuard, PermissionsGuard],
    loadComponent: () =>
      import('./components/user-roles/user-roles.component').then(
        (m) => m.UserRolesComponent
      ),
  },
  {
    path: 'scheduled-jobs',
    canActivate: [MsalGuard, PermissionsGuard],
    loadComponent: () =>
      import('./components/scheduled-jobs/scheduled-jobs.component').then(
        (m) => m.ScheduledJobsComponent
      ),
  },
  {
    path: 'device-models',
    canActivate: [MsalGuard, PermissionsGuard],
    loadComponent: () =>
      import('./components/billing/device-models/device-models-page.component').then(
        (m) => m.DeviceModelsPageComponent
      ),
  },
  {
    path: 'reseller-sku-mappings',
    canActivate: [MsalGuard, PermissionsGuard],
    loadComponent: () =>
      import('./components/billing/reseller-sku-mappings/reseller-sku-mappings.component').then(
        (m) => m.ResellerSkuMappingsComponent
      ),
  },
  {
    path: 'numbers',
    canActivate: [MsalGuard, PermissionsGuard],
    loadComponent: () =>
      import('./components/numbers/numbers.component').then(
        (c) => c.NumbersComponent
      ),
  },
  {
    path: 'trunks',
    canActivate: [MsalGuard, PermissionsGuard],
    loadComponent: () =>
      import('./components/trunks/trunks.component').then(
        (c) => c.TrunksComponent
      ),
  },
  {
    path: `${WizardType.provision}/wizards`,
    canActivate: [MsalGuard, PermissionsGuard],
    loadComponent: () =>
      import(
        './components/wizards/provision-wizards/provision-wizards.component'
      ).then((c) => c.ProvisionWizardsComponent),
  },
  {
    path: `${WizardType.provision}/wizard`,
    canActivate: [MsalGuard, PermissionsGuard],
    loadComponent: () =>
      import('./components/wizards/wizard/wizard.component').then(
        (c) => c.WizardComponent
      ),
  },
  {
    path: `${WizardType.provision}/wizard/:id`,
    canActivate: [MsalGuard, PermissionsGuard],
    loadComponent: () =>
      import('./components/wizards/wizard/wizard.component').then(
        (c) => c.WizardComponent
      ),
  },
  {
    path: `${WizardType.provision}/remediation-wizard/:id`,
    canActivate: [MsalGuard, PermissionsGuard],
    loadComponent: () =>
      import(
        './components/wizards/wizard-remediation/wizard-remediation.component'
      ).then((c) => c.WizardRemediationComponent),
  },
  {
    path: `${WizardType.migration}/wizards`,
    canActivate: [MsalGuard, PermissionsGuard],
    loadComponent: () =>
      import(
        './components/wizards/migration-wizards/migration-wizards.component'
      ).then((c) => c.MigrationWizardsComponent),
  },
  {
    path: `${WizardType.migration}/wizard`,
    canActivate: [MsalGuard, PermissionsGuard],
    loadComponent: () =>
      import('./components/wizards/wizard/wizard.component').then(
        (c) => c.WizardComponent
      ),
  },
  {
    path: `${WizardType.migration}/wizard/:id`,
    canActivate: [MsalGuard, PermissionsGuard],
    loadComponent: () =>
      import('./components/wizards/wizard/wizard.component').then(
        (c) => c.WizardComponent
      ),
  },
  {
    path: `${WizardType.migration}/remediation-wizard/:id`,
    canActivate: [MsalGuard, PermissionsGuard],
    loadComponent: () =>
      import(
        './components/wizards/wizard-remediation/wizard-remediation.component'
      ).then((c) => c.WizardRemediationComponent),
  },
  {
    path: `serviceValidations`,
    canActivate: [MsalGuard, PermissionsGuard],
    loadComponent: () =>
      import(
        './components/service-validations/service-validations.component'
      ).then((c) => c.ServiceValidationsComponent),
  },
  {
    path: `serviceValidations/:id`,
    canActivate: [MsalGuard, PermissionsGuard],
    loadComponent: () =>
      import(
        './components/service-validations/service-validation-sites/service-validation-sites.component'
      ).then((c) => c.ServiceValidationsSitesComponent),
  },
  {
    path: '911-recipients',
    canActivate: [MsalGuard, PermissionsGuard],
    loadComponent: () =>
      import('./components/911-recipients/911-recipients.component').then(
        (c) => c.Notify911RecipientsComponent
      ),
  },
  {
    path: '911-events',
    canActivate: [MsalGuard, PermissionsGuard],
    loadComponent: () =>
      import('./components/911-events/911-events.component').then(
        (c) => c.Notify911EventsComponent
      ),
  },
  {
    path: 'billing',
    canActivate: [MsalGuard, PermissionsGuard],
    component: BillingWizardComponent,
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'dashboard' },
      { path: 'dashboard', component: BillingDashboardComponent },
      { path: 'hosted-summary', component: HostedSummaryComponent },
      { path: 'trunking-summary', component: TrunkingSummaryComponent },
      { path: 'fax-audit', component: FaxAuditComponent },
      { path: 'snapshots', component: HostedSnapshotsComponent },
      { path: 'client-onboarding', component: RecordsListComponent },
      {
        path: 'client-onboarding/wizard',
        component: OnboardingWizardComponent,
      },
      {
        path: 'client-onboarding/wizard/:id',
        component: OnboardingWizardComponent,
      },
      {
        path: 'reconcile',
        loadComponent: () =>
          import(
            './components/billing/reconcile/billing-reconcile.component'
          ).then((m) => m.BillingReconcileComponent),
      },
      {
        path: 'non-billable',
        loadComponent: () =>
          import(
            './components/billing/non-billable/non-billable-page.component'
          ).then((m) => m.NonBillablePageComponent),
      },
      {
        path: 'settings/ns-revio-mappings',
        loadComponent: () =>
          import(
            './components/settings/domain-mapping-settings.component'
          ).then((c) => c.DomainMappingSettingsComponent),
      },
      {
        path: 'providers',
        loadComponent: () =>
          import('./components/billing/providers/providers.component').then(
            (c) => c.ProvidersComponent
          ),
      },
      {
        path: 'services',
        loadComponent: () =>
          import('./components/billing/services/services.component').then(
            (c) => c.ServicesComponent
          ),
      },
      {
        path: 'service-types',
        loadComponent: () =>
          import(
            './components/billing/service-types/service-types.component'
          ).then((c) => c.ServiceTypesComponent),
      },
      {
        path: 'product-types',
        loadComponent: () =>
          import(
            './components/billing/product-types/product-types.component'
          ).then((c) => c.ProductTypesComponent),
      },
      {
        path: 'products',
        loadComponent: () =>
          import('./components/billing/products/products.component').then(
            (c) => c.ProductsComponent
          ),
      },
      {
        path: 'customers',
        loadComponent: () =>
          import('./components/billing/customers/customers.component').then(
            (c) => c.CustomersComponent
          ),
      },
      {
        path: 'customers/:id',
        loadComponent: () =>
          import('./components/billing/customers/customer-detail.component').then(
            (c) => c.CustomerDetailComponent
          ),
      },
    ],
  },
  {
    path: '',
    redirectTo: 'dashboard',
    pathMatch: 'full',
  },
];
